import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import Header from "./Header";
import SliderRating from "./SliderRating";
import ContactForm from "./ContactForm";
import Footer from "./Footer";
import AppStoreButtons from "../../components/AppStoreButtons";
import CustomLink from "./customLink";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import ParentAccordionPage from "../faq-parentPage";
import { pageMetadata } from "../../config/metadata";
import { parentFAQ } from "../../config/faqSchema";
import SEO from "../../Seo";
gsap.registerPlugin(useGSAP);
gsap.registerPlugin(ScrollTrigger);

const ParentLandingPage = () => {
  const metadata = pageMetadata.parentSeo;
    document.title = metadata.title;
    document.description = metadata.description;
  const navigate = useNavigate();

  //jump to the contact form
  const contactFormRef = useRef(null);
  const setFormRef = (ref) => {
    contactFormRef.current = ref;
  };

  const scrollToContactForm = () => {
    if (contactFormRef.current) {
      contactFormRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const [activeTab, setActiveTab] = useState(
    localStorage.getItem("activeTab") || ""
  );
  const [showSection, setShowSection] = useState(
    localStorage.getItem("showSection") || ""
  );

  useEffect(() => {
    const tabObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const newActiveTab = entry.target.id;
          setActiveTab(newActiveTab);
          localStorage.setItem("activeTab", newActiveTab);
          document
            .querySelector(".feature-section")
            .classList.add("active-mode");
        }
      });
    });

    const secObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const newShowSection = entry.target.id;
            setShowSection(newShowSection);
            localStorage.setItem("showSection", newShowSection);
          }
        });
      },
      { rootMargin: "-50px 0px 0px 0px" } // Adjust rootMargin to trigger earlier when scrolling up
    );

    // Observe each tab-section
    document.querySelectorAll(".tab-section").forEach((tab) => {
      tabObserver.observe(tab);
    });

    // Observe each section
    document.querySelectorAll(".section").forEach((sec) => {
      secObserver.observe(sec);
    });

    // Clean up observer on component unmount
    return () => {
      tabObserver.disconnect();
      secObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.pageYOffset === 0) {
        document
          .querySelector(".feature-section")
          .classList.remove("active-mode");
      }
    };

    setTimeout(function () {
      ScrollTrigger.refresh();
    }, 2000);

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  function handleTabClick(name = "", e = null) {
    if (e) {
      e.preventDefault();
    }
    let el = document.getElementById(name);
    if (el) {
      let top = el.getBoundingClientRect().top;
      let y = top + window.scrollY;
      if (top < 0) {
        y = y - 10;
      } else {
        y = y + 10;
      }
      // console.log(name, y, top, window.scrollY);
      window.scroll({
        top: y,
        behavior: "smooth",
      });
    }
  }

  const studentsSlider = {
    dots: true,
    arrows: false,
    infinite: true,
    autoplay: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    initialSlide: 0,
  };

  const buildSlider = {
    dots: false,
    arrows: true,
    infinite: true,
    autoplay: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    initialSlide: 0,
    responsive: [
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          dots: false,
          autoplay: true,
        },
      },
    ],
  };

  useEffect(() => {
    // Animate the #banner-cloud element
    gsap.to("#book-1", {
      duration: 1,
      y: "-=12",
      ease: "Power1.easeInOut",
      yoyo: true,
      repeat: -1,
      delay: 0.5,
    });

    // Animate the #banner-heading element
    gsap.to("#book-2", {
      duration: 1,
      y: "-=12",
      ease: "Power1.easeInOut",
      yoyo: true,
      repeat: -1,
      delay: 0.75,
    });
    gsap.to("#book-3", {
      duration: 1,
      y: "-=12",
      ease: "Power1.easeInOut",
      yoyo: true,
      repeat: -1,
      delay: 1,
    });
  }, []);

  // back to top button set
  useEffect(() => {
    const handleScroll = () => {
      const scrollToTopBtn = document.querySelector(".scrollToTopBtn");
      const rootElement = document.documentElement;

      if (rootElement.scrollTop >= 300) {
        scrollToTopBtn.classList.add("showBtn");
      } else {
        scrollToTopBtn.classList.remove("showBtn");
      }
    };

    const scrollToTop = () => {
      document.documentElement.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    };

    const scrollToTopBtn = document.querySelector(".scrollToTopBtn");
    scrollToTopBtn.addEventListener("click", scrollToTop);
    document.addEventListener("scroll", handleScroll);

    return () => {
      scrollToTopBtn.removeEventListener("click", scrollToTop);
      document.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <>
    <SEO
        title={metadata.title}
        description={metadata.description}
        keywords={metadata.keywords}
        canonical={metadata.canonical}
        ogTitle={metadata.ogTitle}
        ogDescription={metadata.ogDescription}
        ogImage={metadata.ogImage}
        ogUrl={metadata.ogUrl}
        ogType={metadata.ogType}
        twitterCard={metadata.twitterCard}
        twitterTitle={metadata.twitterTitle}
        twitterDescription={metadata.twitterDescription}
        faqData={parentFAQ}
      />
      <div className="back-to-top">
        <button className="scrollToTopBtn">
          <img src="/img/new-home/back-to-top.png" alt="back-to-top" />
        </button>
      </div>

      <section className="parent-banner">
        <div className="container">
          <div className="row">
            <div
              className="col-md-12 col-lg-6 order-change"
              data-aos="zoom-in"
              data-aos-delay="200"
            >
              <h1>Smarter Learning Starts With Practido: Your Ultimate Mock Test & Practice Partner</h1>
              <p>At Practido, we believe real mastery comes from consistent, enjoyable practice. That’s why our entire experience revolves around making every mock test and practice test meaningful, interactive, and motivating. From subject insights to topic-level analytics, Practido gives students and parents full clarity on academic progress without overwhelming them.</p>
              <div
                className="btn-wrapper btn-inline d-none"
                data-aos="zoom-in"
                data-aos-delay="200"
              >
                <button type="btn" className="btn primary-btn">
                  Get Started
                </button>
                <button type="btn" className="btn blue-btn">
                  Already have an Account
                </button>
              </div>
              
              <div className="btn-wrapper btn-inline"><AppStoreButtons variant="white" /></div>
            </div>
            <div
              className="col-md-12 col-lg-6 right-item"
              data-aos="zoom-in"
              data-aos-delay="200"
            >
              <div className="img-box">
                <img
                  className="center-img"
                  src="/img/parent-home/parent-banner.webp"
                  alt="banner"
                />
                <img
                  id="book-1"
                  className="item-1"
                  src="/img/parent-home/book-1.webp"
                  alt="banner"
                />
                <img
                  id="book-2"
                  className="item-2"
                  src="/img/parent-home/book-2.webp"
                  alt="banner"
                />
                <img
                  id="book-3"
                  className="item-3"
                  src="/img/parent-home/book-3.webp"
                  alt="banner"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="parent-our-feature" id="features">
        <div className="container" data-aos="fade-up">
          <div
            className="container top-head"
            data-aos="fade-up"
            data-aos-delay="150"
            data-aos-duration="500"
          >
            <h2>
              Parent's Dashboard and Granular Performance Monitoring
              <span>Upcoming Features in our App</span>
            </h2>
            <p>Our Parent's Dashboard provides you with a real-time insight into your child's progress and achievements. With just a few clicks, you can track their learning journey, view completed tasks, and monitor their overall performance.</p>
            <p><a type='btn' className='btn blue-btn' href="https://play.google.com/store/apps/details?id=com.naskay.olympiadpractice" target="_blank">Help Your Child Learn Better - Start Now!</a></p>
          </div>
          <div className="row aos-init aos-animate" data-aos="fade-up">
            <div
              className="col-md-6 aos-init"
              data-aos="fade-right"
              data-aos-delay="150"
            >
              <div className="img-box">
                <img src="/img/parent-home/parent-feature-1.webp" alt="Subject Score" />
              </div>
            </div>
            <div
              className="col-md-6 aos-init"
              data-aos="fade-left"
              data-aos-delay="150"
            >
              <div className="content">
                <h3>Average Subject Score – Clear Insights That Actually Help</h3>
                <p>
                  Our dashboard gives you a simple, powerful snapshot of how your child performs across subjects. With each mock test and practice test, we highlight strengths, weaknesses, and opportunities for growth, ensuring learning feels less confusing and more guided. It’s easy learning with real purpose.
                </p>
              </div>
            </div>
          </div>
          {/* row */}
          <div
            className="row order-change aos-init aos-animate"
            data-aos="fade-up"
          >
            <div
              className="col-md-6 aos-init"
              data-aos="fade-right"
              data-aos-delay="150"
            >
              <div className="content">
                <h3>Practice Habits: Track Learning Consistency Effortlessly</h3>
                <p>Consistency is the secret sauce behind academic improvement.</p>
                <p>With Practido’s Practice Habits tracker, you can clearly see:</p>
                  <ul>
                    <li>days practiced</li>
                    <li>days missed</li>
                    <li>streaks and engagement patterns</li>
                  </ul>
                <p>It’s the perfect way to keep students accountable while giving parents the tools to support them.</p>
              </div>
            </div>
            <div
              className="col-md-6 aos-init"
              data-aos="fade-left"
              data-aos-delay="150"
            >
              <div className="img-box">
                <img src="/img/parent-home/parent-feature-2.webp" alt="Practice Habits" />
              </div>
            </div>
          </div>
          {/* row */}
          <div className="row aos-init aos-animate" data-aos="fade-up">
            <div
              className="col-md-6 aos-init"
              data-aos="fade-right"
              data-aos-delay="150"
            >
              <div className="img-box">
                <img src="/img/parent-home/parent-feature-3.webp" alt="Detailed Subject" />
              </div>
            </div>
            <div
              className="col-md-6 aos-init"
              data-aos="fade-left"
              data-aos-delay="150"
            >
              <div className="content">
                <h3>Detailed Subject Score Analysis</h3>
                <p>
                  Get a fast breakdown of your child’s performance in every subject. Whether they’re using our free app for mathematics, english, computer, or science, it'll instantly know where they excel and where they need a little extra help.
                </p>
              </div>
            </div>
          </div>
          {/* row */}
          <div
            className="row order-change aos-init aos-animate"
            data-aos="fade-up"
          >
            <div
              className="col-md-6 aos-init"
              data-aos="fade-right"
              data-aos-delay="150"
            >
              <div className="content">
                <h3>Topic-Wise Performance Insights</h3>
                <p>Learning becomes more strategic when you know which topics need work.</p>
                <p>Our analytics show:</p>
                <ul>
                  <li>best-performing topics</li>
                  <li>challenging areas</li>
                  <li>patterns across multiple mock test and practice test attempts</li>
                </ul>
                <p>Perfect for targeted tutoring and smarter revision.</p>
              </div>
            </div>
            <div
              className="col-md-6 aos-init"
              data-aos="fade-left"
              data-aos-delay="150"
            >
              <div className="img-box">
                <img src="/img/parent-home/parent-feature-4.webp" alt="Performance Insights" />
              </div>
            </div>
          </div>
          {/* row */}
          <div className="row aos-init aos-animate" data-aos="fade-up">
            <div
              className="col-md-6 aos-init"
              data-aos="fade-right"
              data-aos-delay="150"
            >
              <div className="img-box">
                <img src="/img/parent-home/parent-feature-5.webp" alt="Track Progress" />
              </div>
            </div>
            <div
              className="col-md-6 aos-init"
              data-aos="fade-left"
              data-aos-delay="150"
            >
              <div className="content">
                <h3>Track Progress Over Time</h3>
                <p>Watch improvement unfold across the platform daily, weekly, monthly, quarterly, or yearly.</p>
                <p>This long-term data helps parents and students make informed decisions and build a strong study routine.</p>
              </div>
            </div>
          </div>
          {/* row */}
          <div
            className="row order-change aos-init aos-animate"
            data-aos="fade-up"
          >
            <div
              className="col-md-6 aos-init"
              data-aos="fade-right"
              data-aos-delay="150"
            >
              <div className="content">
                <h3>Peer Grade Comparison</h3>
                <p>Curious about where your child stands?</p>
                <p>Compare performance with peers across Grade A to Grade F categories, including percentage breakdowns. This builds awareness, motivation, and clarity, all essential for academic confidence.</p>
              </div>
            </div>
            <div
              className="col-md-6 aos-init"
              data-aos="fade-left"
              data-aos-delay="150"
            >
              <div className="img-box">
                <img src="/img/parent-home/parent-feature-6.webp" alt="Peer Grade" />
              </div>
            </div>
          </div>
          {/* row */}
          <div className="row aos-init aos-animate" data-aos="fade-up">
            <div
              className="col-md-6 aos-init"
              data-aos="fade-right"
              data-aos-delay="150"
            >
              <div className="img-box">
                <img src="/img/parent-home/parent-feature-7.webp" alt="Topic Analytics" />
              </div>
            </div>
            <div
              className="col-md-6 aos-init"
              data-aos="fade-left"
              data-aos-delay="150"
            >
              <div className="content">
                <h3>Deep Topic Analytics</h3>
                <p>Unlock rich, actionable insights:</p>
                <ul>
                  <li>average scores</li>
                  <li>total tests taken</li>
                  <li>peer comparison</li>
                  <li>top performer data</li>
                </ul>
                <p>These analytics empower guided learning, especially for students tackling any higher-level mathematical challenge.</p>
              </div>
            </div>
          </div>
          {/* row */}
          <div
            className="row order-change aos-init aos-animate"
            data-aos="fade-up"
          >
            <div
              className="col-md-6 aos-init"
              data-aos="fade-right"
              data-aos-delay="150"
            >
              <div className="content">
                <h3>Practice Test History: Your Learning Journey Made Visible</h3>
                <p>Every attempt tells a story.</p>
                <p>With Practice Test History, students can:</p>
                <ul>
                  <li>revisit old tests</li>
                  <li>observe progress</li>
                  <li>identify recurring weak spots</li>
                  <li>celebrate improvement</li>
                </ul>
                <p>It keeps the learning journey transparent and rewarding.</p>
              </div>
            </div>
            <div
              className="col-md-6 aos-init"
              data-aos="fade-left"
              data-aos-delay="150"
            >
              <div className="img-box">
                <img src="/img/parent-home/parent-feature-8.webp" alt="Test History" />
              </div>
            </div>
          </div>
          {/* row */}
        </div>
      </section>

      <section className="student-slider-wrapper">
        <div className="container">
          <div className="row order-change">
            <div className="col-lg-6">
              <div className="student-slider-container">
                <Slider {...studentsSlider}>
                  <div className="slider-item">
                    <img
                      src="/img/student-home/moniter-slider-1.webp"
                      alt="slider-images"
                    />
                  </div>
                  <div className="slider-item">
                    <img
                      src="/img/student-home/moniter-slider-2.webp"
                      alt="slider-images"
                    />
                  </div>
                  <div className="slider-item">
                    <img
                      src="/img/student-home/moniter-slider-3.webp"
                      alt="slider-images"
                    />
                  </div>
                </Slider>
              </div>
            </div>
            <div className="col-lg-6">
              <h2>Real-Time Student Performance Monitoring</h2>
              <p>Teachers can now monitor daily performance instantly.</p>
              <p>Identify learning gaps as they happen and adjust teaching strategies on the go- no delays, no guesswork.</p>
              {/* <button type='button' className='btn blue-btn' onClick={scrollToContactForm}>Book a Demo</button> */}
              <a
                href="https://calendly.com/shilpi-naskay/30min?month=2023-05"
                target="_blank"
                type="button"
                className="btn blue-btn"
                onClick={scrollToContactForm}
              >
                Book a Demo
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="feature-section">
        <div className="tab-section">
          <div className="section pink-back mb-0 feature-wrapper build-section">
            <div id="reward" className="build-wrapper-box">
              <div className="floating-img">
                <img
                  className="floating-animation reward-0"
                  src="/img/student-home/build-back-sm-0.png"
                  alt="images"
                />
                <img
                  className="floating-animation reward-1"
                  src="/img/student-home/build-back-sm-1.png"
                  alt="images"
                />
                <img
                  className="floating-animation reward-2"
                  src="/img/student-home/build-back-sm-2.png"
                  alt="images"
                />
                <img
                  className="floating-animation reward-3"
                  src="/img/student-home/build-back-sm-3.png"
                  alt="images"
                />
                <img
                  className="floating-animation reward-4"
                  src="/img/student-home/build-back-sm-4.png"
                  alt="images"
                />
                <img
                  className="floating-animation reward-5"
                  src="/img/student-home/build-back-sm-5.png"
                  alt="images"
                />
                <img
                  className="floating-animation reward-6"
                  src="/img/student-home/build-back-sm-4.png"
                  alt="images"
                />
                <img
                  className="floating-animation reward-7"
                  src="/img/student-home/build-back-sm-2.png"
                  alt="images"
                />
                <img
                  className="floating-animation reward-8"
                  src="/img/student-home/build-back-sm-1.png"
                  alt="images"
                />
                <img
                  className="floating-animation reward-9"
                  src="/img/student-home/build-back-sm-6.png"
                  alt="images"
                />
                <img
                  className="floating-animation reward-10"
                  src="/img/student-home/build-back-sm-7.png"
                  alt="images"
                />
              </div>
              <div className="build-wrapper">
                <div className="container">
                  <img
                    className="img-1"
                    data-aos="fade-up"
                    src="/img/student-home/build-mobile-3.webp"
                    alt="streaks"
                  />
                  <img
                    className="img-1 img-2"
                    data-aos="fade-up"
                    src="/img/student-home/build-mobile-2.webp"
                    alt="streaks"
                  />
                  <img
                    className="img-1 img-3"
                    data-aos="fade-up"
                    src="/img/student-home/build-mobile-1.webp"
                    alt="streaks"
                  />
                  <img
                    className="img-1 img-4"
                    data-aos="fade-up"
                    src="/img/student-home/build-mobile-4.webp"
                    alt="streaks"
                  />
                  <div className="data">
                    <h3>Key Features to Enhance Your Learning Journey</h3>
                    <div className="row">
                      <div className="col-md-3">
                        <div className="shimmer-wrapper">
                          <img
                            src="/img/student-home/build-sm-3.png"
                            alt="Points"
                          />
                        </div>
                        <h4>Practice Points</h4>
                        <p>
                          Earn points by taking practice tests, climb the
                          leaderboard, and collect trophies for major
                          milestones.
                        </p>
                      </div>
                      <div className="col-md-3">
                        <div className="shimmer-wrapper">
                          <img
                            src="/img/student-home/build-sm-2.png"
                            alt="Consistency"
                          />
                        </div>
                        <h4>Coins of Consistency</h4>
                        <p>
                          Earn coins through learning activities and unlock
                          exclusive content and features.
                        </p>
                      </div>
                      <div className="col-md-3">
                        <div className="shimmer-wrapper">
                          <img
                            src="/img/student-home/build-sm-4.png"
                            alt="Trophies"
                          />
                        </div>
                        <h4>Earn Trophies</h4>
                        <p>
                          Scale the leaderboard with Practido Points, earn
                          trophies for achievements, and excel in your learning
                          journey.
                        </p>
                      </div>
                      <div className="col-md-3">
                        <div className="shimmer-wrapper">
                          <img
                            src="/img/student-home/build-sm-1.png"
                            alt="streaks"
                          />
                        </div>
                        <h4>Practice Streaks</h4>
                        <p>
                          Build consistency and boost success with our streak
                          feature, rewarding daily engagement.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mobile-build-box">
                <div className="build-wrapper-mobile">
                  <div className="data">
                    <div className="row">
                      <Slider className="col-lg-12" {...buildSlider}>
                        <div className="slider-item">
                          <img
                            className="img-1"
                            src="/img/student-home/build-mobile-3.webp"
                            alt="Points"
                          />
                          <div className="slider-data">
                            <div className="mb-head">
                              Key Features to Enhance Your Learning Journey
                            </div>
                            <div className="shimmer-wrapper">
                              <img
                                src="/img/student-home/build-sm-3.png"
                                alt="Points"
                              />
                            </div>
                            <h4>Practice Points</h4>
                            <p>
                              Earn points by taking practice tests, climb the
                              leaderboard, and collect trophies for major
                              milestones.
                            </p>
                          </div>
                        </div>
                        <div className="slider-item item-1">
                          <img
                            className="img-1 img-2"
                            src="/img/student-home/build-mobile-2.webp"
                            alt="Consistency"
                          />
                          <div className="slider-data">
                            <div className="mb-head">
                              Key Features to Enhance Your Learning Journey
                            </div>
                            <div className="shimmer-wrapper">
                              <img
                                src="/img/student-home/build-sm-2.png"
                                alt="Consistency"
                              />
                            </div>
                            <h4>Coins of Consistency</h4>
                            <p>
                              Earn coins through learning activities and unlock
                              exclusive content and features.
                            </p>
                          </div>
                        </div>
                        <div className="slider-item item-2">
                          <img
                            className="img-1 img-4"
                            src="/img/student-home/build-mobile-4.webp"
                            alt="streaks"
                          />
                          <div className="slider-data">
                            <div className="mb-head">
                              Key Features to Enhance Your Learning Journey
                            </div>
                            <div className="shimmer-wrapper">
                              <img
                                src="/img/student-home/build-sm-4.png"
                                alt="Trophies"
                              />
                            </div>
                            <h4>Earn Trophies</h4>
                            <p>
                              Scale the leaderboard with Practido Points, earn
                              trophies for achievements, and excel in your
                              learning journey.
                            </p>
                          </div>
                        </div>
                        <div className="slider-item">
                          <img
                            className="img-1 img-3"
                            src="/img/student-home/build-mobile-1.webp"
                            alt="Trophies"
                          />
                          <div className="slider-data">
                            <div className="mb-head">
                              Key Features to Enhance Your Learning Journey
                            </div>
                            <div className="shimmer-wrapper">
                              <img
                                src="/img/student-home/build-sm-1.png"
                                alt="streaks"
                              />
                            </div>
                            <h4>Practice Streaks</h4>
                            <p>
                              Build consistency and boost success with our
                              streak feature, rewarding daily engagement.
                            </p>
                          </div>
                        </div>
                      </Slider>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="parent-pdf-section">
        <div className="feature-wrapper">
          <div className="container">
            <div className="feature-box row">
              <div className="col-md-12 col-lg-7">
                <div className="img-box">
                  <img
                    className="feature-img"
                    src="/img/parent-home/parent-pdf.webp"
                    alt="pdf-downloads"
                  />
                  <img
                    id="book-2"
                    className="cloud"
                    src="/img/parent-home/pdf-1.webp"
                    alt="cloud"
                  />
                  <img
                    id="book-3"
                    className="folder"
                    src="/img/parent-home/pdf-2.webp"
                    alt="folder"
                  />
                </div>
              </div>
              <div className="col-md-12 col-lg-5 para-box">
                <h2>Offline PDF Downloads: Learn Anytime, Anywhere</h2>
                <p>No internet? No problem.</p>
                <p>Students can download their <span className="bold-txt">mock test</span>, worksheets, and practice PDFs to study offline. Perfect during travel, low-connectivity areas, or screen-free time. It’s flexible, portable, and student-friendly.</p>
                <CustomLink
                to="/login"
                type="button"
                className="btn link-btn"
              >
                Download Free Worksheets for Easy Revision
              </CustomLink>
                <div className="btn-wrapper btn-inline"><AppStoreButtons variant="white" /></div>
              </div>
            </div>
            {/* feature-box */}
          </div>
        </div>
      </section>

      <section className="parent-practido">
        <div className="container">
          <div className="container aos-animate" data-aos="fade-up">
            <div
              className="head"
              data-aos="fade-up"
              data-aos-delay="150"
              data-aos-duration="500"
            >
              <h2>Join the Worldwide Practido Online Olympiad <span>Step into the Practido Online Olympiad from the comfort of your home! Compete, improve, and enjoy a fully interactive learning experience packed with value.<br/>Why Students Love It:</span></h2>
            </div>
            <div className="row">
              <div className="col-sm-12 col-md-4">
                <div className="img-box">
                  <img
                    src="/img/parent-home/practido-1.webp"
                    alt="focused practice"
                  />
                  <img
                    className="first-img zoom-in-zoom-out"
                    src="/img/parent-home/practido-1_1.webp"
                    alt="focused practice"
                  />
                  <img
                    className="sec-img zoom-in-zoom-out"
                    src="/img/parent-home/practido-1_2.webp"
                    alt="focused practice"
                  />
                </div>
                <h3>Focused Practice</h3>
                <p>We prioritize concept-building, not random tricky questions.</p>
                <p>Every <span className="bold">mock test</span> and practice test is crafted to strengthen understanding.</p>
              </div>
              <div className="col-sm-12 col-md-4">
                <div className="img-box">
                  <img
                    src="/img/parent-home/practido-2.webp"
                    alt="Engaging Participation"
                  />
                  <img
                    className="bulb-img zoom-in-zoom-out"
                    src="/img/parent-home/practido-2_1.webp"
                    alt="focused practice"
                  />
                </div>
                <h3>Fun, Engaging Participation</h3>
                <p>Turn practice into competition! Students enjoy healthy rivalry that boosts motivation.</p>
              </div>
              <div className="col-sm-12 col-md-4">
                <div className="img-box">
                  <img
                    src="/img/parent-home/practido-3.webp"
                    alt="Relevant Questions"
                  />
                  <img
                    className="first-qus zoom-in-zoom-out"
                    src="/img/parent-home/Question_Mark-left.webp"
                    alt="Relevant Questions"
                  />
                  <img
                    className="sec-qus zoom-in-zoom-out"
                    src="/img/parent-home/Question_Mark-left.webp"
                    alt="Relevant Questions"
                  />
                  <img
                    className="left-first-qus zoom-in-zoom-out"
                    src="/img/parent-home/Question_Mark-right.webp"
                    alt="Relevant Questions"
                  />
                  <img
                    className="left-sec-qus zoom-in-zoom-out"
                    src="/img/parent-home/Question_Mark-right-2.webp"
                    alt="Relevant Questions"
                  />
                </div>
                <h3>Meaningful Questions</h3>
                <p>Expect real-world application, comprehension, and logical reasoning, not mere memorization.</p>
              </div>
            </div>
            <div className="center-btn">
              <button
                type="button"
                className="btn blue-btn"
                onClick={() => window.open("/register", "_blank")}
              >
                Register Now
              </button>
            </div>
          </div>
        </div>
      </section>

      <SliderRating contactFormRef={contactFormRef} />
      <section className="faq-main-section faq-container">
        <div className="faq-data">
          <div className="container">
            <h3>Frequently Asked Questions</h3>       
            <div className="update-faq">
              <ParentAccordionPage></ParentAccordionPage>
            </div>
          </div>
        </div>
      </section>
      <ContactForm contactFormRef={setFormRef} />
    </>
  );
};

export default ParentLandingPage;
